import { getDisplayName, getBugHeading, getBlockerDisplayName } from '@hooks/projects/projectHelpers';

/**
 * Convert rich text editor content to API format: replace #mention-* links with
 * {{user:id}}, {{milestone:id}}, {{sprint:id}}, {{task:id}}, {{bug:id}}, {{blocker:id}}.
 * Used by DiscussionPanel, milestone modals, ProjectSprintsTab.
 */
export function contentToApiMentions(html) {
    if (!html || typeof html !== 'string') return html;
    let out = html;
    out = out.replace(/<a\s[^>]*href="#mention-user-([^"]*)"[^>]*>[\s\S]*?<\/a>/gi, '{{user:$1}}');
    out = out.replace(/<a\s[^>]*href="#mention-milestone-([^"]*)"[^>]*>[\s\S]*?<\/a>/gi, '{{milestone:$1}}');
    out = out.replace(/<a\s[^>]*href="#mention-sprint-([^"]*)"[^>]*>[\s\S]*?<\/a>/gi, '{{sprint:$1}}');
    out = out.replace(/<a\s[^>]*href="#mention-task-([^"]*)"[^>]*>[\s\S]*?<\/a>/gi, '{{task:$1}}');
    out = out.replace(/<a\s[^>]*href="#mention-bug-([^"]*)"[^>]*>[\s\S]*?<\/a>/gi, '{{bug:$1}}');
    out = out.replace(/<a\s[^>]*href="#mention-blocker-([^"]*)"[^>]*>[\s\S]*?<\/a>/gi, '{{blocker:$1}}');
    return out;
}

/** Escape for HTML attribute / text content in mention links. */
function escapeMentionAttr(s) {
    return String(s)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

/**
 * Replace API `{{user:id}}` … placeholders in stored HTML with display links (same output as DiscussionPanel).
 * @param {string} html
 * @param {object} [descriptionMentions] — API shape: `{ users, milestones, sprints, tasks, bugs, blockers }`
 */
export function resolveDescriptionMentionsForDisplay(html, descriptionMentions) {
    if (!html || typeof html !== 'string') return html;
    const dm = descriptionMentions && typeof descriptionMentions === 'object' ? descriptionMentions : {};
    const mentions = Array.isArray(dm.users) ? dm.users : [];
    const milestones = Array.isArray(dm.milestones) ? dm.milestones : [];
    const sprints = Array.isArray(dm.sprints) ? dm.sprints : [];
    const tasks = Array.isArray(dm.tasks) ? dm.tasks : [];
    const bugs = Array.isArray(dm.bugs) ? dm.bugs : [];
    const blockers = Array.isArray(dm.blockers) ? dm.blockers : [];
    const userMap = new Map(mentions.map((m) => [String(m.id), m]));
    const milestoneMap = new Map(milestones.map((m) => [String(m.id), m]));
    const sprintMap = new Map(sprints.map((s) => [String(s.id), s]));
    const taskMap = new Map(tasks.map((t) => [String(t.id), t]));
    const bugMap = new Map(bugs.map((b) => [String(b.id), b]));
    const blockerMap = new Map(blockers.map((b) => [String(b.id), b]));
    const esc = escapeMentionAttr;
    let out = html;
    out = out.replace(/\{\{user:([^}]+)\}\}/g, (_, id) => {
        const user = userMap.get(id.trim());
        const displayName = user ? getDisplayName(user) : 'User';
        return `<a href="#mention-user-${id.trim()}" data-mention="user" data-username="${esc(displayName)}" contenteditable="false">@${esc(displayName)}</a>`;
    });
    out = out.replace(/\{\{milestone:([^}]+)\}\}/g, (_, id) => {
        const m = milestoneMap.get(id.trim());
        const displayName = m?.name || 'Milestone';
        return `<a href="#mention-milestone-${id.trim()}" data-mention="milestone" data-name="${esc(displayName)}" contenteditable="false">@${esc(displayName)}</a>`;
    });
    out = out.replace(/\{\{sprint:([^}]+)\}\}/g, (_, id) => {
        const s = sprintMap.get(id.trim());
        const displayName = s?.name || 'Sprint';
        return `<a href="#mention-sprint-${id.trim()}" data-mention="sprint" data-name="${esc(displayName)}" contenteditable="false">@${esc(displayName)}</a>`;
    });
    out = out.replace(/\{\{task:([^}]+)\}\}/g, (_, id) => {
        const t = taskMap.get(id.trim());
        const displayName = t?.name ?? t?.title ?? 'Task';
        return `<a href="#mention-task-${id.trim()}" data-mention="task" data-name="${esc(displayName)}" contenteditable="false">@${esc(displayName)}</a>`;
    });
    out = out.replace(/\{\{bug:([^}]+)\}\}/g, (_, id) => {
        const b = bugMap.get(id.trim());
        const displayName = b ? getBugHeading(b, 48) : 'Bug';
        return `<a href="#mention-bug-${id.trim()}" data-mention="bug" data-name="${esc(displayName)}" contenteditable="false">@${esc(displayName)}</a>`;
    });
    out = out.replace(/\{\{blocker:([^}]+)\}\}/g, (_, id) => {
        const b = blockerMap.get(id.trim());
        const displayName = b ? getBlockerDisplayName(b, 48) : 'Blocker';
        return `<a href="#mention-blocker-${id.trim()}" data-mention="blocker" data-name="${esc(displayName)}" contenteditable="false">@${esc(displayName)}</a>`;
    });
    return out;
}
